<template>
  <div class="detail-dialog__price" v-if="billingCyclePriceNumList.length > 0">
    <Price v-for="billingCyclePriceItem in billingCyclePriceNumFilterList"
           :key="billingCyclePriceItem.price"
           class="detail-dialog__price-item"
           v-bind="$attrs"
           :currency="currencySymbol"
           :price="billingCyclePriceItem.price"
           :unit="billingCyclePriceItem.unit"/>
  </div>
</template>
<script>
import {
  BILLING_CYCLE,
  BillingCycleEnum,
  planTypes,
  NO_NEED_CHANGE_PRICE_CYCLE,
  enableProAnnualMonthlyPaymentPlanP2,
} from "@/common/constants";
import { proAnnualMonthlyMarketingCampaignLimited } from '@/utils/annual-term-monthly-utils';
import {compact} from "lodash";
import Price from "@/components/price.vue";
import {getCurrency, getCurrencySymbol} from "@/utils/currency";
import {mapState, mapGetters} from "vuex";
import { getPlanUnitWithCycle, planUnitMap, getBillTextByCycle } from '@/common/product-unit';
import {ONLINE_NOT_SUPPORT_BILLING_CYCLE} from "@/common/service/non-standard-billing-cycle-utils";
import {MIGRATE_FLOW_TYPES} from "@/plans/ZOOM_MEETING/dialog/migration-plan";
import { billingCycleToValueMap } from '@/utils';

export default {
  name: "price-list",
  components: {Price},
  props: {
    planCategory: String,
    unitType: {
      type: Number,
      default: planUnitMap.user
    },
  },
  data() {
    return {
      currencySymbol: getCurrencySymbol(getCurrency()),
    }
  },
  methods:{
    getBilledPriceIfNeed(billingCycle, amount) {
      if (NO_NEED_CHANGE_PRICE_CYCLE.includes(billingCycle)) {
        return '';
      }
      return this.$money(amount, { forceDiff: true })
    },
  },
  computed: {
    ...mapState({
      planMapFlat: (state) => state.ShoppingCart.planMapFlat,
    }),
    ...mapGetters(['currentEditPlan', 'migratePlanState', 'getSelectedPlans', 'boughtPlans', 'freeTrialPlans']),
    enableBillingCycleList() {
      let billingCycleList = [BILLING_CYCLE.MONTHLY, BILLING_CYCLE.QUARTERLY, BILLING_CYCLE.ANNUAL, BILLING_CYCLE.TWO_YEARS, BILLING_CYCLE.THREE_YEARS];
      // * for phase 2, support annual term monthly
      if ( enableProAnnualMonthlyPaymentPlanP2) {
        billingCycleList.splice(1, 0, BILLING_CYCLE.ANNUAL_TERM_MONTHLY);
      }
      if ([MIGRATE_FLOW_TYPES.biz2bundle, MIGRATE_FLOW_TYPES.pro2bundle].includes(this.migratePlanState?.migrateType)) {
        const bizPlusPlan = this.getSelectedPlans.find(p => p.type === planTypes.ZOOM_ONE_BUNDLE)
        const fistBillingCycle = (bizPlusPlan?.billing_cycle && bizPlusPlan.billing_cycle !== BILLING_CYCLE.ANNUAL) ? bizPlusPlan.billing_cycle : BILLING_CYCLE.MONTHLY
        billingCycleList = [fistBillingCycle, BILLING_CYCLE.ANNUAL]
      } else if (this.currentEditPlan && ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(this.currentEditPlan.billing_cycle)) {
        billingCycleList = [this.currentEditPlan.billing_cycle, BILLING_CYCLE.ANNUAL]
      }
      billingCycleList.sort((a, b) => BillingCycleEnum[a] - BillingCycleEnum[b])
      return billingCycleList
    },
    billingCyclePriceNumFilterList() {
      // Only filter if the list contains ANNUAL_TERM_MONTHLY and conditions are met
      const hasAnnualTermMonthly = this.billingCyclePriceNumList.some(item => item.billingCycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY);

      if (hasAnnualTermMonthly && proAnnualMonthlyMarketingCampaignLimited()) {
        return this.billingCyclePriceNumList.filter(item => item.billingCycle !== BILLING_CYCLE.ANNUAL_TERM_MONTHLY);
      }

      return this.billingCyclePriceNumList;
    },
    billingCyclePriceNumList() {
      return compact(this.enableBillingCycleList.map(billingCycle => {
        const billingCycleListContainAnnualTermMonthly = this.enableBillingCycleList.includes(BILLING_CYCLE.ANNUAL_TERM_MONTHLY);

        const plan = this.planMapFlat.find(plan => plan.category === this.planCategory && plan.billing_cycle === billingCycle);
        if (plan) {
          const amount = plan.billing_cycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY ? plan.amount / plan.cycleValue : plan.amount;
          let unitText = '';
          let planUnit = getPlanUnitWithCycle(plan.billing_cycle, this.unitType);
          let price = this.$money(amount, { forceDiff: true });
          if (!enableProAnnualMonthlyPaymentPlanP2) {
            unitText = planUnit;
          } else {
            if (billingCycleListContainAnnualTermMonthly) {
              if (billingCycle === BILLING_CYCLE.ANNUAL_TERM_MONTHLY) {
                unitText = `${planUnit}, ${this.$t('billing.bill_12_payments')}, ${getBillTextByCycle(billingCycle)}`
              } else if (billingCycle === BILLING_CYCLE.ANNUAL) {
                planUnit = getPlanUnitWithCycle(BILLING_CYCLE.MONTHLY, this.unitType);
                unitText = `${planUnit}, ${this.getBilledPriceIfNeed(billingCycle, amount)} ${getBillTextByCycle(billingCycle)}`;
                price = this.$money(amount / billingCycleToValueMap[billingCycle], { forceDiff: true });
              } else {
                unitText = planUnit;
              }
            } else {
              unitText = planUnit;
            }
          }

          return {
            price: price,
            unit: unitText,
            billingCycle,
          };
        }
        return null;
      }));
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/_variables.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';

.detail-dialog {
  &__price {
    width: 100%;
    display: flex;
    flex-direction: row;
    white-space: nowrap;

    p.price.price-component {
      //flex-wrap: nowrap;
      @include media(mobile, tablet) {
        flex-wrap: wrap;
        align-content: flex-start;
      }
    }

    &-item {
      // flex: 1;
      padding-right: 24px;
      padding-left: 0;
      display: flex;
      align-items: flex-start;

      .price__currency {
        font-style: normal;
        font-weight: 500;
        font-size: 14px;
        line-height: 36px;
        color: #00053D;
        margin-right: 2px;
      }

      .price__int {
        font-family: $happy-display;
        font-style: normal;
        font-weight: 400;
        font-size: 32px;
        line-height: 48px;
        color: #00053D;
        margin-right: 2px;
      }

      .price__decimal {
        font-style: normal;
        font-weight: 500;
        font-size: 14px;
        line-height: 36px;
        color: #00053D;
        margin-right: 2px;
      }

      .price__unit {
        font-style: normal;
        font-weight: 450;
        font-size: 16px;
        line-height: 38px;
        color: #00053D;
        align-self: flex-end;
      }

      & + & {
        padding-right: 0;
        padding-left: 24px;
        border-left: 2px solid #B8B8C4;
      }
    }
  }
}
</style>
